import ContactList from '@/data/models/domain/ContactList'
import ContactListFolder from '@/data/models/domain/contactGrouping/ContactListFolder'
import Contact from '@/data/models/domain/Contact'
import type { NotFoundResolverParams, ProgressResolverParams, FacetResolverParams } from '@/services/resolvers/types'
import type { Resolvers } from '@/services/types'
import { contactImportIdRouteParam, ListIdRouteParam } from '@/components/views/contacts/types'
import ContactImport from '@/data/models/domain/contactImport/ContactImport'
import type { DomainTitlerParams } from '@/services/route/titlers/types'
import UnsubscribedContact from '@/data/models/domain/UnsubscribedContact'
import ContactImportResult from '@/data/models/domain/contactImport/ContactImportResult'
import { AppModule } from '@/config/types'
import type { TmNamedRouteRecord } from '@/services/route/types'
import { contactsMyRoute } from '@/routes/user/contacts/contactsMy'
import { Segment } from '@/data/models/domain/filteredViews/Segment'
import { SEGMENT_DRAFT_NAME_PARAM_NAME, SEGMENT_ID_PARAM_NAME } from '@/composition/domain/contacts/segments/types'
import AsyncResolver from '@/components/resolvers/AsyncResolver.vue'
import type { CleanModelsResolverParams } from '@/services/resolvers/cleanModelsResolver'
import type { ContactFacet } from '@/services/domain/contact/types'
import {
  CONTACT_LIST_FOLDER_ID_PARAM_NAME,
  contactsListFolderContactListRouteName,
  contactsListFolderRouteName,
  contactsListsFoldersContactListRouteName,
  contactsListsFoldersRouteName,
} from '@/routes/user/contacts/types'
import type { ContactListFolderSummaryResolverParams } from '@/services/domain/contactGrouping/types'

export const contactsUnsubscribedSmsRouteName = 'user.contacts.unsubscribed.sms'

export const contactsUnsubscribedEmailRouteName = 'user.contacts.unsubscribed.email'

export const contactListDetailsRouteName = 'user.contacts.lists.contactListDetails'

const createContactListDetailsRoute = (
  name: string,
  path: string,
  listsRouteName: string,
  extraResolvers: Resolvers = [],
): TmNamedRouteRecord => ({
  name,
  path,
  props: (route) => {
    const id = route.params[ListIdRouteParam]
    return {
      /**
       * Needed for update list, after changing route https://textmagic.atlassian.net/browse/CAR-9215
       */
      key: id,
      contactListId: id,
    }
  },
  meta: {
    resolverComponent: AsyncResolver,
    resolvers: [
      {
        service: 'ContactListResolver',
        params: {
          source: 'router',
          requestParam: ListIdRouteParam,
          model: ContactList,
          redirectToIfNotFound: { name: listsRouteName },
          cache: false,
        } as NotFoundResolverParams,
      },
      ...extraResolvers,
    ],
    titler: {
      service: 'DomainTitlerService',
      params: {
        model: ContactList,
        field: 'name',
        idParamName: ListIdRouteParam,
      } as DomainTitlerParams,
    },
    redirectToIfNotFound: { name: listsRouteName },
    modules: [AppModule.ContactListCounter, AppModule.Compose],
    fullHeight: true,
  },
  component: () => import('@/components/views/contacts/contactLists/ContactListDetails.vue'),
})

export const contactsListsRoute: TmNamedRouteRecord = {
  name: 'user.contacts.lists.index',
  path: 'lists',
  meta: {
    tags: ['CONTACTS_BAR_LIST'],
    accessControlGroups: ['ContactListsStrategy'],
    resolvers: [
      {
        service: 'FacetResolver',
        params: {
          model: Contact,
          subscriptionModels: [ContactList],
          types: ['hiddenList'] satisfies Array<keyof ContactFacet>,
        } satisfies FacetResolverParams,
      },
    ],
  },
  redirect: { name: 'user.contacts.lists' },
  children: [
    {
      name: 'user.contacts.lists',
      path: '',
      meta: {
        tabTitle: 'pageTabTitle.user.contacts.lists',
        modules: [AppModule.Compose],
      },
      component: () => import('@/components/views/contacts/contactLists/ContactLists.vue'),
    },
    createContactListDetailsRoute(contactListDetailsRouteName, `:${ListIdRouteParam}`, 'user.contacts.lists'),
  ],
}

export const contactsListsFoldersRoute: TmNamedRouteRecord = {
  name: 'user.contacts.listsFolders.index',
  path: 'folders',
  meta: {
    tags: ['CONTACTS_BAR_LIST'],
    accessControlGroups: ['FoldersStrategy'],
    modules: [AppModule.Compose, AppModule.ContactGroupingItem],
    resolvers: [
      {
        service: 'FacetResolver',
        params: {
          model: Contact,
          subscriptionModels: [ContactList],
          types: ['hiddenList'] satisfies Array<keyof ContactFacet>,
        } satisfies FacetResolverParams,
      },
      {
        service: 'FetchAllAccountsResolver',
      },
    ],
    resolverComponent: AsyncResolver,
  },
  redirect: { name: contactsListsFoldersRouteName },
  children: [
    {
      name: contactsListsFoldersRouteName,
      path: '',
      meta: {
        tabTitle: 'pageTabTitle.user.contacts.listsFolders',
      },
      component: () => import('@/components/views/contacts/contactListsFolders/ContactListsFolders.vue'),
    },
    {
      name: contactsListFolderRouteName,
      path: `folder/:${CONTACT_LIST_FOLDER_ID_PARAM_NAME}`,
      props: (route) => {
        const folderId = route.params[CONTACT_LIST_FOLDER_ID_PARAM_NAME]
        return {
          key: folderId,
          folderId,
        }
      },
      meta: {
        tabTitle: 'pageTabTitle.user.contacts.listsFolders',
        titler: {
          service: 'DomainTitlerService',
          params: {
            model: ContactListFolder,
            idParamName: CONTACT_LIST_FOLDER_ID_PARAM_NAME,
            noRecordTranslateKey: 'pageTabTitle.user.contacts.listsFolders',
          } as DomainTitlerParams,
        },
        nonBlockingResolvers: [
          {
            service: 'ContactListFolderSummaryResolver',
            params: {
              requestParam: CONTACT_LIST_FOLDER_ID_PARAM_NAME,
              redirectToIfNotFound: { name: contactsListsFoldersRouteName },
            } satisfies ContactListFolderSummaryResolverParams,
          },
        ],
      },
      component: () => import('@/components/views/contacts/contactListsFolders/ContactListFolder.vue'),
    },
    createContactListDetailsRoute(
      contactsListsFoldersContactListRouteName,
      `list/:${ListIdRouteParam}`,
      contactsListsFoldersRouteName,
    ),
    createContactListDetailsRoute(
      contactsListFolderContactListRouteName,
      `folder/:${CONTACT_LIST_FOLDER_ID_PARAM_NAME}/list/:${ListIdRouteParam}`,
      contactsListsFoldersRouteName,
      [
        {
          service: 'ContactListFolderSummaryResolver',
          params: {
            requestParam: CONTACT_LIST_FOLDER_ID_PARAM_NAME,
            redirectToIfNotFound: { name: contactsListsFoldersRouteName },
          } satisfies ContactListFolderSummaryResolverParams,
        },
      ],
    ),
  ],
}

export const contactsSegmentDetailsRoute: TmNamedRouteRecord = {
  name: 'user.contacts.segments.details',
  path: `:${SEGMENT_ID_PARAM_NAME}`,
  props: (route) => {
    const segmentId = route.params[SEGMENT_ID_PARAM_NAME]

    return {
      segmentId,
      key: segmentId,
    }
  },
  meta: {
    titler: {
      service: 'DomainTitlerService',
      params: {
        model: Segment,
        field: 'name',
        idParamName: SEGMENT_ID_PARAM_NAME,
      } as DomainTitlerParams,
    },
    modules: [AppModule.FilteredViews, AppModule.Segments],
    resolverComponent: AsyncResolver,
    resolvers: [
      {
        service: 'RedirectableSingleModelResolverService',
        params: {
          model: Segment,
          source: 'router',
          requestParam: SEGMENT_ID_PARAM_NAME,
          redirectToIfNotFound: { name: 'user.contacts.segments.index' },
        },
      },
    ],
  },
  component: () => import('@/components/views/contacts/segments/SegmentDetails.vue'),
}

export const contactsSegmentCreateRoute: TmNamedRouteRecord = {
  name: 'user.contacts.segments.new',
  path: 'new',
  props: (route) => ({
    name: route.query[SEGMENT_DRAFT_NAME_PARAM_NAME],
  }),
  meta: {
    modules: [AppModule.FilteredViews, AppModule.Segments],
    resolvers: [{ service: 'SegmentCreateParamsResolverService' }],
  },
  component: () => import('@/components/views/contacts/segments/SegmentCreate.vue'),
}

export const contactsSegmentsRoute: TmNamedRouteRecord = {
  name: 'user.contacts.segments.index',
  path: 'segments',
  meta: {
    tabTitle: 'pageTitle.user.contacts.segments',
    tags: ['CONTACTS_BAR_LIST'],
    modules: [
      AppModule.FilteredViews,
      AppModule.Segments,
      AppModule.MessageSend,
      AppModule.Compose,
      AppModule.ComposeEmail,
    ],
  },
  redirect: { name: 'user.contacts.segments' },
  children: [
    {
      name: 'user.contacts.segments',
      path: '',
      component: () => import('@/components/views/contacts/segments/Segments.vue'),
    },
    contactsSegmentDetailsRoute,
    contactsSegmentCreateRoute,
  ],
}

export const contactsTagsRoute: TmNamedRouteRecord = {
  name: 'user.contacts.tags',
  path: 'tags',
  meta: {
    tags: ['CONTACTS_BAR_LIST'],
    tabTitle: 'pageTabTitle.user.contacts.tags',
  },
  component: () => import('@/components/views/contacts/tags/Tags.vue'),
}

export const contactsNotesRoute: TmNamedRouteRecord = {
  name: 'user.contacts.notes',
  path: 'notes',
  meta: {
    tags: ['CONTACTS_BAR_LIST'],
    tabTitle: 'pageTabTitle.user.contacts.notes',
    modules: [AppModule.Note, AppModule.NoteFeed, AppModule.Chat, AppModule.Tickets],
    accessControlGroups: ['NotesFeedStrategy'],
    resolvers: [{ service: 'NoteFeedScopeResolver' }],
  },
  component: () => import('@/components/views/contacts/notes/NotesFeed.vue'),
}

export const contactsImportsRoute: TmNamedRouteRecord = {
  name: 'user.contacts.imports.index',
  path: 'imports',
  meta: {
    tags: ['CONTACTS_BAR_LIST'],
    permission: 'importContacts',
    resolvers: [
      {
        service: 'ProgressResolver',
        params: {
          serviceIds: ['ContactImportsService', 'EmailLookupSessionService'],
        } satisfies ProgressResolverParams,
      },
    ],
  },
  redirect: { name: 'user.contacts.imports' },
  children: [
    {
      name: 'user.contacts.imports',
      path: '',
      meta: {
        modules: [AppModule.ContactsImportList, AppModule.ContactsImportDetails],
        tabTitle: 'pageTabTitle.user.contacts.imports',
      },
      component: () => import('@/components/views/contacts/imports/Imports.vue'),
    },
    {
      name: 'user.contacts.imports.details',
      path: `details/:${contactImportIdRouteParam}`,
      props: true,
      meta: {
        tabTitle: 'pageTabTitle.user.contacts.importDetails',
        resolverComponent: AsyncResolver,
        resolvers: [
          {
            service: 'ContactsImportDetailsResolverService',
            params: {
              source: 'router',
              requestParam: contactImportIdRouteParam,
              model: ContactImport,
              redirectToIfNotFound: { name: 'user.contacts.imports' },
              cache: false,
            } as NotFoundResolverParams,
          },
          {
            service: 'FacetResolver',
            params: {
              model: ContactImportResult,
              source: 'router',
              requestParam: contactImportIdRouteParam,
              subscriptionModels: [ContactImportResult],
            },
          },
          {
            service: 'CleanModelsResolver',
            params: {
              models: [ContactImportResult],
            } satisfies CleanModelsResolverParams,
          },
        ],
        modules: [AppModule.ContactsImportDetails, AppModule.EmailLookupSession],
        parent: 'user.contacts.imports',
      },
      component: () => import('@/components/views/contacts/imports/ImportDetails.vue'),
      redirect: { name: 'user.contacts.imports.details.imported' },
      children: [
        {
          name: 'user.contacts.imports.details.imported',
          path: 'imported',
          component: () => import('@/components/views/contacts/imports/results/ContactsImported.vue'),
        },
        {
          name: 'user.contacts.imports.details.duplicates',
          path: 'duplicates',
          component: () => import('@/components/views/contacts/imports/results/ContactsDuplicates.vue'),
        },
        {
          name: 'user.contacts.imports.details.failed',
          path: 'failed',
          component: () => import('@/components/views/contacts/imports/results/ContactsFailed.vue'),
        },
        {
          name: 'user.contacts.imports.details.excluded',
          path: 'excluded',
          component: () => import('@/components/views/contacts/imports/results/ContactsExcluded.vue'),
        },
        {
          name: 'user.contacts.imports.details.unsubscribed',
          path: 'unsubscribed',
          component: () => import('@/components/views/contacts/imports/results/ContactsUnsubscribed.vue'),
        },
      ],
    },
    {
      name: 'user.contacts.import',
      path: 'import-contacts',
      meta: {
        resolvers: [{ service: 'ContactImportResolver' }],
        modules: [
          AppModule.ContactsImportDetails,
          AppModule.ContactsImportFlow,
          AppModule.CarrierLookup,
          AppModule.EmailLookupSession,
        ],
        tabTitle: 'pageTabTitle.user.contacts.import',
      },
      component: () => import('@/components/views/contacts/import/ContactImport.vue'),
    },
  ],
}

export const contactsUnsubscribedSmsRoute: TmNamedRouteRecord = {
  name: contactsUnsubscribedSmsRouteName,
  path: '',
  component: () => import('@/components/views/contacts/unsubscribed/UnsubscribedSms.vue'),
}

export const contactsUnsubscribedEmailRoute: TmNamedRouteRecord = {
  name: contactsUnsubscribedEmailRouteName,
  path: 'email',
  component: () => import('@/components/views/contacts/unsubscribed/UnsubscribedEmail.vue'),
}

export const contactsUnsubscribedRoute: TmNamedRouteRecord = {
  name: 'user.contacts.unsubscribed',
  path: 'unsubscribed',
  meta: {
    tabTitle: 'pageTabTitle.user.contacts.unsubscribed',
    tags: ['CONTACTS_BAR_LIST'],
    resolvers: [
      {
        service: 'FacetResolver',
        params: {
          model: Contact,
          subscriptionModels: [UnsubscribedContact],
          types: ['unsubscriber', 'unsubscriberEmail'] satisfies Array<keyof ContactFacet>,
        } satisfies FacetResolverParams,
      },
    ],
  },
  component: () => import('@/components/views/contacts/unsubscribed/Unsubscribed.vue'),
  redirect: { name: contactsUnsubscribedSmsRoute.name },
  children: [contactsUnsubscribedSmsRoute, contactsUnsubscribedEmailRoute],
}

export const contactsBlockedRoute: TmNamedRouteRecord = {
  name: 'user.contacts.blockedContacts',
  path: 'blocked',
  meta: {
    tabTitle: 'pageTabTitle.user.contacts.blocked',
    tags: ['CONTACTS_BAR_LIST'],
  },
  component: () => import('@/components/views/contacts/blockedContacts/BlockedContacts.vue'),
}

export const contactsRoute: TmNamedRouteRecord = {
  name: 'user.contacts',
  path: 'contacts',
  redirect: { name: contactsMyRoute.name },
  meta: {
    tags: ['USER_TOP_MENU'],
    tabTitle: 'pageTitle.user.contacts.contacts',
    permission: 'contacts',
    resolvers: [
      {
        service: 'FilteredViewServerEventsResolverService',
      },
      { service: 'PinnedContactsResolverService' },
    ],
    resolverComponent: AsyncResolver,
    modules: [
      AppModule.Contacts,
      AppModule.ContactList,
      AppModule.ContactCounter,
      AppModule.ContactsUnsubscribed,
      AppModule.ContactsBlocked,
      AppModule.FilteredViews,
      AppModule.Segments,
      AppModule.PinnedContacts,
      AppModule.Tags,
    ],
  },
  component: () => import('@/components/views/contacts/Contacts.vue'),
  children: [
    contactsMyRoute,
    contactsListsRoute,
    contactsListsFoldersRoute,
    contactsSegmentsRoute,
    contactsTagsRoute,
    contactsNotesRoute,
    contactsImportsRoute,
    contactsUnsubscribedRoute,
    contactsBlockedRoute,
  ],
}
